import { useState, useEffect, CompositionEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  usePatient,
  useSelectPatient,
  useVerifyPatientInvitationLastName,
} from '@/hooks/patient.hooks';
import { useInvitation } from '@/hooks/useInvitation';
import {
  updateInvitationId,
  updateQuestionnaireId,
  updateQuestionnaireRevision,
  updateConsultationVariant,
  updatePatient,
} from '@/reducers/graph';
import { useInjectConsultation } from '@/hooks/useInjectConsultation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { LETTERS_WITH_DIACRITIC_REGEX } from '@aldebaran/common/util/regex';
import { formatPracticeWelcome } from '@/util/practice';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import BlignyLogo from '@/assets/BlignyLogo.png';
import { LandingPageFooter } from '@/pages/onboarding/LandingPageFooter';

export const IdentificationModale = ({
  invitationId,
  comingFromUnsubscribe = false,
  showSubmitButton = false,
}: {
  invitationId?: string;
  comingFromUnsubscribe?: boolean;
  showSubmitButton?: boolean;
}) => {
  const dispatch = useDispatch();
  const { navigateNext } = useQuestionnaireNavigation();
  const professional = useSelector(
    (state: RootState) => state.consultation.professional,
  );
  const practice = useSelector(
    (state: RootState) => state.consultation.practice,
  );
  const navigate = useNavigate();
  const injectConsultation = useInjectConsultation();
  const {
    mutateAsync: verifyLastName,
    mutate: verifyLastNameMutate,
    data: verifyStatus,
    error: verifyError,
    isLoading: isVerifyPending,
  } = useVerifyPatientInvitationLastName(comingFromUnsubscribe);
  const [error, setError] = useState('');
  const [lastName, setLastName] = useState<string>('');
  const { data: invitation } = useInvitation(invitationId || '');
  const [patientId, setPatientId] = useState<string | null>(null);
  const { data: patient } = usePatient(patientId);
  const { mutate: selectPatient } = useSelectPatient();
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (
      !showSubmitButton &&
      !isComposing &&
      invitationId &&
      lastName.length > 1
    ) {
      verifyLastName({
        invitationId,
        lastName: lastName,
      });
    }
  }, [lastName, isComposing, invitationId, verifyLastName, showSubmitButton]);

  useEffect(() => {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    if ((verifyError as any)?.response?.data?.message === 'Too many tries') {
      console.error('otp too many tries');
      setError('too-many-tries');
    } else if (verifyError || verifyStatus?.data.status === 'error') {
      console.error('otp error');
      setError('error');
    } else if (verifyStatus?.data.status === 'fail') {
      console.error('otp fail');
      setError('fail');
    } else if (
      verifyStatus?.data.status === 'success' &&
      invitation?.consultation.patient.id
    ) {
      console.log('otp success');
      setError('');
      setPatientId(invitation.consultation.patient.id);
    } else {
      setError('');
    }
  }, [verifyStatus, verifyError, invitation, setPatientId, setError]);

  useEffect(() => {
    if (patient && verifyStatus) {
      const questionnaireId = verifyStatus.data?.questionnaireId;
      if (comingFromUnsubscribe) {
        dispatch(updatePatient(patient));
        navigate('/unsub-confirm');
      } else if (invitation && questionnaireId) {
        dispatch(updateInvitationId(invitation.id));
        dispatch(updateQuestionnaireId(questionnaireId));
        dispatch(updateQuestionnaireRevision(verifyStatus.data.revision));
        // The server may have switched the consultation to its continuation
        // variant (a borne check-in continuing on the phone, ALDO-2759)
        if (verifyStatus.data.variant)
          dispatch(updateConsultationVariant(verifyStatus.data.variant));
        injectConsultation({ date: invitation.consultation.date });
        selectPatient(patient.id, {
          onSuccess: () => {
            navigateNext();
          },
        });
      }
    }
  }, [
    patient,
    verifyStatus,
    invitation,
    comingFromUnsubscribe,
    dispatch,
    navigate,
    injectConsultation,
    selectPatient,
    // navigateNext is intentionally omitted to avoid infinite loops as it changes reference frequently due to NavigationProvider dependencies
  ]);

  const handleChange = (value: string) => {
    console.log('otp input');
    if (isComposing) {
      setLastName(value);
    } else if (value === '' || LETTERS_WITH_DIACRITIC_REGEX.test(value)) {
      setLastName(value);
    }
  };

  // For crazy people using compose keys
  const handleCompositionStart = () => {
    setIsComposing(true);
  };
  const handleCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    handleChange(e.currentTarget.value);
  };

  const handleValidateClick = () => {
    if (invitationId && lastName.length >= 2) {
      verifyLastNameMutate({ invitationId, lastName });
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className={`${
          showSubmitButton ? 'h-[440px] w-[340px]' : 'h-[350px] w-[326px]'
        } no-scrollbar justify-center`}
      >
        <div
          className={`p-6 flex flex-col items-center justify-center h-full ${
            error
              ? showSubmitButton
                ? 'gap-0'
                : 'gap-3'
              : showSubmitButton
              ? 'gap-2'
              : 'gap-7'
          }`}
        >
          {!showSubmitButton && (
            <div className="flex w-full justify-center items-center">
              {comingFromUnsubscribe ? (
                <span className="text-xl font-semibold text-indigo-900 text-center">
                  Se désinscrire des communications
                </span>
              ) : professional ? (
                // TODO: we need to have a dynamic logo stored in the practice model
                practice?.name.includes('Bligny') ? (
                  <img src={BlignyLogo} alt="Bligny Logo" className="w-50" />
                ) : practice?.onboardingDisplayMode === 'PRACTICE' &&
                  practice?.name ? (
                  <p className="text-xl font-light text-center">
                    {formatPracticeWelcome(practice)}
                  </p>
                ) : (
                  <p className="text-xl font-light text-center">
                    Bienvenue chez le <br />
                    <span className="text-xl font-semibold text-primaryBlue text-center">
                      {professional.title} {professional.firstname}{' '}
                      {professional?.lastname}
                    </span>
                  </p>
                )
              ) : (
                <p>Bienvenue</p>
              )}
            </div>
          )}

          <div
            className={`flex flex-col items-center w-70 ${
              error
                ? showSubmitButton
                  ? 'h-50'
                  : 'h-70'
                : showSubmitButton
                ? 'h-40'
                : ''
            } p-4 rounded-xl gap-4`}
          >
            <div className="flex flex-col items-center gap-2">
              <p className="w-full text-center text-sm text-indigo-900 font-medium">
                Entrez les 4 premières lettres
                <br />
                du nom de famille du patient :
              </p>
              <div
                className={`flex flex-col items-center rounded-md ${
                  error ? 'bg-red-100 gap-1 p-2' : 'bg-transparent'
                }`}
              >
                <div className="flex gap-2 justify-center">
                  <InputOTP
                    maxLength={isComposing ? 5 : 4} // to allow composing characters on the last slot
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    value={lastName}
                    onChange={handleChange}
                    disabled={error === 'error' || error === 'too-many-tries'}
                    autoComplete="off"
                    className={`flex gap-2 justify-center [&_input]:focus:outline-none [&_input]:focus:ring-0 ${
                      error ? 'bg-red-25 border-red-300' : 'border-defaultColor'
                    }`}
                  >
                    <InputOTPGroup className="flex gap-2">
                      <InputOTPSlot
                        index={0}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[0]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={1}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[1]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={2}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[2]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={3}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[3]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {error && (
                  <p className="w-52 text-left text-red-600 text-xs">
                    {error === 'error' && 'Une erreur est survenue.'}
                    {error === 'too-many-tries' &&
                      'Vous avez effectué trop de tentatives.'}
                    {error === 'fail' &&
                      'Les informations renseignées ne correspondent pas.'}
                  </p>
                )}
              </div>
            </div>
            <div
              className={`w-full flex flex-col items-center gap-1 text-sm font-light text-[#848282] ${
                showSubmitButton && error ? 'mt-4' : ''
              }`}
            >
              <p>
                Marie <span className="font-medium">Curi</span>e :{' '}
                <span className="font-medium">CURI</span>
              </p>
              <p>
                François <span className="font-medium">Le Dr</span>an :{' '}
                <span className="font-medium">LEDR</span>
              </p>
              <p>
                Charles <span className="font-medium">Kao</span> :{' '}
                <span className="font-medium">KAO</span>
              </p>
            </div>
          </div>

          {showSubmitButton && (
            <div className="w-full flex flex-col items-center">
              <LandingPageFooter
                consultationDate={undefined}
                buttonLabel="Commencer en sécurité"
                onClick={handleValidateClick}
                disabled={lastName.length < 2 || isVerifyPending}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
